import React, { useState, useRef, useEffect, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Info, Pencil, ChevronUp, ChevronDown, ExternalLink, CheckSquare, CalendarCheck, Search, X, Check, Archive } from 'lucide-react';
import { useLeads } from '../../context/LeadsContext';
import { useSettings } from '../../context/SettingsContext';
import { useCompany } from '../../context/CompanyContext';
import { formatDate, formatAddress, truncateText } from '../../utils/helpers';
import { getDisplayName } from '../../lib/utils';
import { matchesQuery } from '../../utils/contactSearch';
import LeadInfoPopover from './LeadInfoPopover';
import FlashPill from '../propertyDetails/FlashPill';
import { getStageColorClasses } from '../../lib/stageColors';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '../ui/input';

const PipelineKanban = ({ onEditLead }) => {
  const { leads, updateLead, ensureLeadsSince } = useLeads();
  const { companyId } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const { ARCHIVED_STAGE } = useSettings();
  const [selectedLead, setSelectedLead] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // ─── Bulk-move state ────────────────────────────────────────────────
  // When `selectMode` is on, cards render checkboxes, drag is disabled,
  // and a floating action bar exposes a "Move to → …" batch operation.
  // Selection is a Set of lead ids scoped to the current session.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkTargetStage, setBulkTargetStage] = useState('');
  const [bulkMoving, setBulkMoving] = useState(false);

  // ─── Year filter ────────────────────────────────────────────────────
  // Prevents the board from becoming an endless year-over-year backlog.
  // Default: the current calendar year. Users can toggle to any year that
  // has data, or "All years". No records are deleted; older leads are
  // simply hidden until the operator selects that year.
  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState(String(currentYear));

  // Incremental column rendering — large columns render 50 cards at a time
  const COLUMN_RENDER_CHUNK = 50;
  const [visibleCounts, setVisibleCounts] = useState({});
  const visibleFor = (stage) => visibleCounts[stage] || COLUMN_RENDER_CHUNK;
  const showMoreFor = (stage) =>
    setVisibleCounts((p) => ({ ...p, [stage]: (p[stage] || COLUMN_RENDER_CHUNK) + COLUMN_RENDER_CHUNK }));

  // Expand the bounded leads window when an older year or "All years" is selected
  useEffect(() => {
    if (yearFilter === 'all') {
      ensureLeadsSince(null);
    } else {
      const y = parseInt(yearFilter, 10);
      if (!Number.isNaN(y)) ensureLeadsSince(new Date(y, 0, 1));
    }
  }, [yearFilter, ensureLeadsSince]);
  // Lead id to highlight after navigation from /leads/:id "View in Pipeline".
  // Cleared automatically after ~2.5s so the ring doesn't linger.
  const [highlightLeadId, setHighlightLeadId] = useState(null);
  const scrollContainerRef = useRef(null);
  const ghostScrollRef = useRef(null);
  const leadCardRefs = useRef({});
  // Once we've consumed an incoming `highlightLeadId` from router state we
  // must NOT re-consume it within the same session.
  const consumedHighlightRef = useRef(null);
  // Highlight timers live on a ref so they survive re-renders triggered by
  // navigate({state:{}}) below. The cleanup of a re-running effect would
  // otherwise cancel the pending highlight before it becomes visible.
  const highlightTimersRef = useRef({ scroll: null, clear: null, wipe: null });

  // Canonical Lead pipeline stages — sales funnel only.
  // Operational state (scheduled, in_progress, completed) lives on the Job (type='job' task).
  const CANONICAL_STAGES = [
    "New Lead",
    "In Progress",
    "Quoted",
    "Approved",
    "Booked",
    "Archived"
  ];

  const pipelineStages = CANONICAL_STAGES;

  // Stage normalization mapping: legacy / removed stages → current canonical stages
  const STAGE_ID_MAP = {
    "New": "New Lead",
    "New Lead": "New Lead",
    "In Progress": "In Progress",
    "Quoted": "Quoted",
    "Approved": "Approved",
    "Booked": "Booked",
    "Voicemail": "In Progress",    // legacy → mapped
    "Follow-Up": "In Progress",    // legacy → mapped
    "Scheduled": "Booked",         // removed stage → Booked
    "Completed": "Booked",         // removed stage → Booked (job.status drives completion)
    "Archived": "Archived"
  };

  // Sync scroll between ghost scrollbar and columns
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const ghostScroll = ghostScrollRef.current;

    if (!scrollContainer || !ghostScroll) return;

    const handleContainerScroll = () => {
      ghostScroll.scrollLeft = scrollContainer.scrollLeft;
    };

    const handleGhostScroll = () => {
      scrollContainer.scrollLeft = ghostScroll.scrollLeft;
    };

    scrollContainer.addEventListener('scroll', handleContainerScroll);
    ghostScroll.addEventListener('scroll', handleGhostScroll);

    return () => {
      scrollContainer.removeEventListener('scroll', handleContainerScroll);
      ghostScroll.removeEventListener('scroll', handleGhostScroll);
    };
  }, []);

  // Pick up `highlightLeadId` from navigation state (e.g. profile → "View in
  // Pipeline"), scroll the matching card into view, and clear the router
  // state so back/refresh don't keep re-highlighting.
  //
  // We use a ref guard so each incoming highlightLeadId is consumed exactly
  // once. Timers are stored in refs so they survive the re-render triggered
  // by navigate({state: {}}) below — otherwise the effect's cleanup would
  // cancel them before they fire.
  useEffect(() => {
    const targetId = location.state?.highlightLeadId;
    if (!targetId) return undefined;
    if (!leads || leads.length === 0) return undefined;
    if (consumedHighlightRef.current === targetId) return undefined;

    consumedHighlightRef.current = targetId;
    setHighlightLeadId(targetId);

    // Defer scroll until the column + card have mounted.
    highlightTimersRef.current.scroll = setTimeout(() => {
      const node = leadCardRefs.current[targetId];
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }, 150);

    highlightTimersRef.current.clear = setTimeout(() => {
      setHighlightLeadId(null);
      consumedHighlightRef.current = null;
    }, 2500);

    // Wipe router state AFTER the highlight ring has rendered + scroll fired,
    // so we don't trigger a re-mount/render that wipes our local state. By
    // the time this fires the ref guard is also set so it won't re-consume.
    highlightTimersRef.current.wipe = setTimeout(() => {
      navigate(location.pathname, { replace: true, state: {} });
    }, 600);

    return undefined;
  }, [location.state?.highlightLeadId, leads.length, location.pathname, navigate]);

  // Cancel pending highlight timers on unmount only.
  useEffect(() => {
    return () => {
      const timers = highlightTimersRef.current;
      if (timers.scroll) clearTimeout(timers.scroll);
      if (timers.clear) clearTimeout(timers.clear);
      if (timers.wipe) clearTimeout(timers.wipe);
    };
  }, []);

  // Normalise a lead's createdAt into a JS Date (or null). Firestore
  // Timestamps expose .toDate(); ISO strings parse via Date. Leads created
  // before this field existed return null and are counted under the
  // current year so they don't disappear from the default view.
  const getLeadYear = (lead) => {
    const raw = lead?.createdAt;
    if (!raw) return currentYear;
    try {
      const d = raw?.toDate ? raw.toDate() : new Date(raw);
      const y = d.getFullYear();
      return Number.isFinite(y) ? y : currentYear;
    } catch {
      return currentYear;
    }
  };

  // Years present in the data, sorted DESC. Always includes the current
  // year so the default filter chip has content even on an empty account.
  const availableYears = useMemo(() => {
    const set = new Set([currentYear]);
    leads.forEach((l) => set.add(getLeadYear(l)));
    return Array.from(set).sort((a, b) => b - a);
  }, [leads, currentYear]);

  // Apply search filter first (cheap; runs once per keystroke, <500 leads).
  // Phone-normalized: "203-909-6881", "(203) 909-6881", "2039096881" all match.
  const filteredLeads = useMemo(() => {
    let list = leads;

    // Year filter — "all" bypasses; otherwise match on createdAt year.
    if (yearFilter !== 'all') {
      const y = Number(yearFilter);
      list = list.filter((lead) => getLeadYear(lead) === y);
    }

    // Search filter — only apply when a query is present.
    if (searchTerm) {
      list = list.filter((lead) =>
        matchesQuery(lead, searchTerm, {
          resolvedName: getDisplayName(lead, ''),
          extraTextFields: ['service'],
        })
      );
    }

    return list;
  }, [leads, searchTerm, yearFilter]);

  // Group filtered leads by stage with normalization
  const leadsByStage = {};
  pipelineStages.forEach((stage) => {
    leadsByStage[stage] = filteredLeads.filter((lead) => {
      const normalizedStage = STAGE_ID_MAP[lead.stage] || "New Lead";
      return normalizedStage === stage;
    });
  });

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    // Dropped outside or in same position
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const newStage = destination.droppableId;
    const oldStage = source.droppableId;

    try {
      // Default: update lead stage
      await updateLead(draggableId, { stage: newStage });
    } catch (error) {
      console.error('Error updating lead stage:', error);
    }
  };

  // ─── Bulk-select handlers ───────────────────────────────────────────
  const toggleSelected = (leadId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setBulkTargetStage('');
  };

  // Move every selected lead to the target stage. Uses the same
  // `updateLead` call as drag-and-drop so any downstream side effects
  // (e.g. unscheduling on transition into 'Approved') are preserved.
  const handleBulkMove = async () => {
    if (!bulkTargetStage || selectedIds.size === 0 || bulkMoving) return;
    setBulkMoving(true);
    try {
      for (const id of selectedIds) {
        try {
          await updateLead(id, { stage: bulkTargetStage });
        } catch (err) {
          console.error('[PipelineKanban] bulk move failed for', id, err);
        }
      }
      exitSelectMode();
    } finally {
      setBulkMoving(false);
    }
  };

  const LeadCard = ({ lead, index }) => {
    const [showPopover, setShowPopover] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const isArchived = lead.stage === ARCHIVED_STAGE;
    const isHighlighted = highlightLeadId === lead.id;
    const isSelected = selectedIds.has(lead.id);

    return (
      <Draggable
        draggableId={lead.id}
        index={index}
        isDragDisabled={selectMode}
      >
        {(provided, snapshot) => (
          <div
            ref={(node) => {
              provided.innerRef(node);
              if (node) {
                leadCardRefs.current[lead.id] = node;
              } else {
                delete leadCardRefs.current[lead.id];
              }
            }}
            {...provided.draggableProps}
            {...(selectMode ? {} : provided.dragHandleProps)}
            data-testid={`pipeline-lead-card-${lead.id}`}
            onClick={selectMode ? () => toggleSelected(lead.id) : undefined}
            className={`relative bg-white rounded-lg border p-3 mb-3 shadow-sm transition-all ${
              selectMode ? 'cursor-pointer' : ''
            } ${
              isSelected
                ? 'border-[var(--cb-orange)] ring-2 ring-[var(--cb-orange)] ring-offset-1'
                : 'border-gray-200'
            } ${
              snapshot.isDragging
                ? 'shadow-lg scale-105 rotate-2'
                : !isArchived && !selectMode && 'hover:border-[var(--cb-orange)] hover:shadow-md'
            } ${collapsed ? 'py-2' : ''} ${isArchived ? 'opacity-70' : ''} ${
              isHighlighted ? 'ring-2 ring-[var(--cb-orange)] ring-offset-2 animate-pulse' : ''
            }`}
            style={{
              ...provided.draggableProps.style,
            }}
          >
            {/* Bulk-select checkbox — visible only in select mode */}
            {selectMode && (
              <div
                className={`absolute top-2 left-2 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                  isSelected
                    ? 'bg-[var(--cb-orange)] border-[var(--cb-orange)]'
                    : 'bg-white border-gray-300'
                }`}
                data-testid={`pipeline-select-checkbox-${lead.id}`}
              >
                {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
              </div>
            )}
            {/* Header Icons: Collapse, Edit, Open Tasks, Open Profile.
                Hidden in bulk-select mode so a tap on the card selects it
                cleanly without competing with per-icon click handlers. */}
            {!selectMode && (
            <div className="absolute top-1 right-2 flex items-center gap-1">
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setCollapsed((prev) => !prev);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title={collapsed ? 'Expand card' : 'Collapse card'}
              >
                {collapsed ? (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                )}
              </button>

              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditLead?.(lead);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Edit lead"
              >
                <Pencil className="h-4 w-4 text-gray-400 hover:text-[var(--cb-orange)]" />
              </button>

              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPopover(!showPopover);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Open tasks"
              >
                <CheckSquare className="h-4 w-4 text-gray-400 hover:text-[var(--cb-orange)]" />
              </button>

              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/leads/${lead.id}`);
                }}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Open lead profile"
              >
                <ExternalLink className="h-4 w-4 text-gray-400 hover:text-[var(--cb-orange)]" />
              </button>
            </div>
            )}

            {/* Info Popover - inline mini card */}
            {showPopover && (
              <div className="absolute top-7 right-2 z-20">
                <LeadInfoPopover
                  lead={lead}
                  onClose={() => setShowPopover(false)}
                  position="right"
                  variant="inline"
                />
              </div>
            )}

            {/* Lead Name (always visible) */}
            <h4
              className={`font-semibold text-sm ${selectMode ? 'pl-7 pr-2' : 'pr-20'}`}
              style={{ color: 'var(--cb-navy)' }}
            >
              {getDisplayName(lead, 'Unnamed Lead')}
            </h4>

            {/* Flash pill — bright property warning, always visible (incl. collapsed) */}
            {lead.propertyDetails?.flashNote && (
              <div className="mt-1">
                <FlashPill
                  propertyDetails={lead.propertyDetails}
                  size="sm"
                  testId={`kanban-flash-${lead.id}`}
                />
              </div>
            )}

            {/* Expanded content */}
            {!collapsed && (
              <>
                {/* Address */}
                <p className="text-xs text-gray-600 mb-2 mt-1">
                  {formatAddress(lead.address)}
                </p>

                {/* Service */}
                {lead.service && (
                  <div className="mb-2">
                    <span
                      className="inline-block px-2 py-1 text-xs rounded"
                      style={{
                        backgroundColor: 'rgba(242, 107, 29, 0.1)',
                        color: 'var(--cb-orange)',
                      }}
                    >
                      {lead.service}
                    </span>
                  </div>
                )}

                {/* Notes Preview */}
                {lead.notes && (
                  <p className="text-xs text-gray-500 mb-2 italic">
                    {truncateText(lead.notes, 50)}
                  </p>
                )}

                {/* Dates */}
                <div className="flex justify-between text-xs text-gray-400 pt-2 border-top border-gray-100">
                  <span>Created: {formatDate(lead.createdAt)}</span>
                  <span>Contact: {formatDate(lead.lastContact)}</span>
                </div>

                {/* Schedule CTA for Approved stage */}
                {(STAGE_ID_MAP[lead.stage] === 'Approved' || lead.stage === 'Approved') && (
                  <button
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/leads/${lead.id}`);
                    }}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors"
                    data-testid={`schedule-job-btn-${lead.id}`}
                  >
                    <CalendarCheck className="h-3.5 w-3.5" />
                    Schedule Job
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top controls: search on the left, filters + bulk toggle on the right */}
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        {/* Search bar — office-call-lookup: name / phone / address / email */}
        <div className="flex items-center gap-2 max-w-md flex-1 min-w-[240px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, phone, address, email…"
              className="pl-9 pr-8 h-9"
              data-testid="pipeline-search-input"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100"
                data-testid="pipeline-search-clear"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
          {searchTerm && (
            <span
              className="text-xs text-gray-500 whitespace-nowrap"
              data-testid="pipeline-search-count"
            >
              {filteredLeads.length} match{filteredLeads.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          {/* Year filter — defaults to the current calendar year so the
              board doesn't become an endless year-over-year backlog.
              "All years" is available for search across history. */}
          <div className="flex items-center gap-1.5" data-testid="pipeline-year-filter">
            <Archive className="h-4 w-4 text-gray-400" />
            <label htmlFor="pipeline-year-select" className="sr-only">Pipeline year</label>
            <select
              id="pipeline-year-select"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cb-orange)] focus:border-transparent"
              data-testid="pipeline-year-select"
            >
              <option value="all">All years</option>
              {availableYears.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </div>

          {/* Bulk-select toggle. Once active, cards render checkboxes and a
              floating action bar (bottom-center) shows the batch move. */}
          <button
            type="button"
            onClick={() => (selectMode ? exitSelectMode() : setSelectMode(true))}
            className={`h-9 px-3 rounded-md border text-sm font-medium transition-colors ${
              selectMode
                ? 'bg-[var(--cb-orange)] text-white border-[var(--cb-orange)]'
                : 'bg-white text-gray-700 border-gray-200 hover:border-[var(--cb-orange)] hover:text-[var(--cb-orange)]'
            }`}
            data-testid="pipeline-bulk-select-toggle"
          >
            {selectMode ? 'Cancel selection' : 'Select'}
          </button>
        </div>
      </div>

      {/* Ghost Scrollbar */}
      <div
        ref={ghostScrollRef}
        className="overflow-x-auto mb-2"
        style={{ height: '12px' }}
      >
        <div style={{ width: `${pipelineStages.length * 320}px`, height: '1px' }} />
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto pb-4"
          style={{ height: 'calc(100vh - 200px)' }}
        >
          {pipelineStages.map((stage) => {
            const isArchivedColumn = stage === ARCHIVED_STAGE;
            return (
              <Droppable key={stage} droppableId={stage}>
                {(provided, snapshot) => (
                  <div
                    className="flex-shrink-0"
                    style={{ width: '320px' }}
                  >
                    {/* Column Header - Sticky */}
                    <div
                      className="sticky top-0 z-10 border border-gray-200 rounded-lg p-3 mb-3 shadow-sm"
                      style={{
                        backgroundColor: isArchivedColumn
                          ? 'rgba(148, 163, 184, 0.15)' // dimmer for Archived
                          : snapshot.isDraggingOver
                          ? 'rgba(242, 107, 29, 0.05)'
                          : 'white',
                      }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h3
                          className="font-bold text-sm flex-1 truncate"
                          style={{ color: 'var(--cb-navy)' }}
                        >
                          {stage}
                        </h3>
                        {/* Stage count pill — visible at every scale.
                            Reads live from the filtered board so the number
                            always matches what the CSR sees in the column. */}
                        <span
                          className={`inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 text-xs font-semibold rounded-full ${
                            isArchivedColumn
                              ? 'bg-gray-200 text-gray-600'
                              : (leadsByStage[stage]?.length || 0) > 0
                                ? 'bg-[rgba(242,107,29,0.12)] text-[var(--cb-orange)]'
                                : 'bg-gray-100 text-gray-400'
                          }`}
                          data-testid={`pipeline-stage-count-${stage}`}
                        >
                          {leadsByStage[stage]?.length || 0}
                        </span>
                      </div>
                    </div>

                    {/* Cards Container - Scrollable */}
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="overflow-y-auto"
                      style={{ height: 'calc(100% - 60px)' }}
                    >
                      {(leadsByStage[stage] || []).slice(0, visibleFor(stage)).map((lead, index) => (
                        <LeadCard key={lead.id} lead={lead} index={index} />
                      ))}
                      {provided.placeholder}

                      {/* Incremental rendering — counts above always reflect
                          the FULL column; only card DOM nodes are capped. */}
                      {(leadsByStage[stage]?.length || 0) > visibleFor(stage) && (
                        <button
                          type="button"
                          onClick={() => showMoreFor(stage)}
                          className="w-full py-2 my-1 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          data-testid={`pipeline-show-more-${stage}`}
                        >
                          Show more ({leadsByStage[stage].length - visibleFor(stage)} remaining)
                        </button>
                      )}

                      {/* Empty State */}
                      {(!leadsByStage[stage] || leadsByStage[stage].length === 0) && (
                        <div className="text-center text-gray-400 text-sm py-8">
                          No leads in this stage
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>

      {/* ─── Floating Bulk Move bar ────────────────────────────────────
          Appears only while `selectMode` is on. Batch-moves every selected
          lead through the same updateLead(id, {stage}) call the drag path
          uses, preserving all downstream side effects (e.g. Approved →
          unschedule linked jobs). Non-blocking: cards remain interactive
          for further selection until the operator clicks Move. */}
      {selectMode && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 bg-white border border-gray-200 rounded-full shadow-lg px-4 py-2"
          data-testid="pipeline-bulk-action-bar"
        >
          <span className="text-sm font-medium text-gray-700" data-testid="pipeline-bulk-selected-count">
            {selectedIds.size} selected
          </span>
          <div className="h-5 w-px bg-gray-200" />
          <label htmlFor="pipeline-bulk-target" className="sr-only">Move selected leads to stage</label>
          <select
            id="pipeline-bulk-target"
            value={bulkTargetStage}
            onChange={(e) => setBulkTargetStage(e.target.value)}
            className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cb-orange)] focus:border-transparent"
            data-testid="pipeline-bulk-target-select"
          >
            <option value="">Move to…</option>
            {pipelineStages.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleBulkMove}
            disabled={!bulkTargetStage || selectedIds.size === 0 || bulkMoving}
            className="h-8 px-3 rounded-md text-sm font-medium text-white bg-[var(--cb-orange)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
            data-testid="pipeline-bulk-move-btn"
          >
            {bulkMoving ? 'Moving…' : 'Move'}
          </button>
          <button
            type="button"
            onClick={exitSelectMode}
            className="h-8 w-8 flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
            data-testid="pipeline-bulk-cancel"
            aria-label="Cancel selection"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default PipelineKanban;
